<template>
  <b-sidebar
    :id="id"
    ref="sidebar"
    :visible="isSidebarActive"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >

    <template #default="{ hide }">
      <b-overlay
        v-if="isSidebarActive"
        :show="isLoading"
        rounded="sm"
        variant="secondary"
        opacity="0.75"
      >
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5
            v-t="`modules.addresses.pages.add`"
            class="mb-0"
          />

          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />

        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="refFormObserver"
        >
          <!-- Body -->
          <b-form
            class="p-2"
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <div class="mb-2">
              <i-input
                id="firstName"
                v-model="itemData.first_name"
                :errors="validationErrors.first_name"
                type="text"
                title="inputs.first_name"
                required
              />
            </div>

            <div class="mb-2">
              <i-input
                id="lastName"
                v-model="itemData.last_name"
                :errors="validationErrors.last_name"
                type="text"
                title="inputs.last_name"
                required
              />
            </div>

            <div class="mb-2">
              <i-input
                id="mobile"
                v-model="itemData.mobile"
                :errors="validationErrors.mobile"
                type="tel"
                title="inputs.mobile"
                :dial-code="itemData.mobile_code"
                required
                @update:dial-code="val => itemData.mobile_code = val"
              />
            </div>

            <div class="mb-2">
              <i-input
                v-model="itemData.country_code"
                :errors="validationErrors.country_code"
                type="select"
                title="inputs.country"
                :options="countryOptions"
                :clearable="true"
                :reduce="country => country.id"
                required
                @input="c => fetchStates(c)"
              />
            </div>

            <div class="mb-2">

              <i-input
                v-model="itemData.state_id"
                type="select"
                title="inputs.state"
                :options="stateOptions"
                :clearable="true"
                label="name"
                :reduce="i => i.id"
                required
              />
            </div>

            <div class="mb-2">
              <i-input
                v-model="itemData.city_name"
                :errors="validationErrors.city_name"
                type="text"
                title="inputs.city"
                required
              />
            </div>

            <div class="mb-2">
              <i-input
                v-model="itemData.line_1"
                :errors="validationErrors.line_1"
                type="text"
                title="modules.addresses.details.address_line_1"
                required
              />
            </div>

            <div class="mb-2">
              <i-input
                v-model="itemData.line_2"
                :errors="validationErrors.line_2"
                type="text"
                title="modules.addresses.details.address_line_2"
              />
            </div>

            <div class="mb-2">
              <i-input
                v-model="itemData.postal_code"
                :errors="validationErrors.postal_code"
                type="text"
                title="modules.addresses.details.postal_code"
              />
            </div>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                v-t="(itemId || itemData.id)?`Edit`:`Add`"
                variant="primary"
                class="mr-2"
                type="submit"
              />
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                v-t="`Cancel`"
                variant="outline-secondary"
                @click="hide"
              />
            </div>
          </b-form>
        </validation-observer>

      </b-overlay>

    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BButton, BOverlay, BCol,
} from 'bootstrap-vue'
import { onUnmounted, onMounted, ref } from 'vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import store from '@/store'
import useModelUpdate from '@/views/models/common/useModelUpdate'
import formValidation from '@core/comp-functions/forms/form-validation'
import addressStoreModule from '@/views/models/addresses/addressStoreModule'
import customerStoreModule from '@/views/models/customers/customerStoreModule'

export default {
  components: {
    BCol,
    ValidationObserver,
    BSidebar,
    BForm,
    BButton,
    BOverlay,
  },
  directives: {
    Ripple,
  },
  emits: ['refetch-data'],
  model: {
    prop: 'isSidebarActive',
    event: 'update:is-sidebar-active',
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    id: {
      type: String,
      default: 'sidebar-add-new-address',
      required: false,
    },
    itemId: {
      type: Number,
      default: null,
    },
    customerId: {
      type: String,
      default: null,
      required: false,
    },
    defaultData: {
      type: Object,
      default: Object,
    },
  },
  watch: {
    itemId(newValue) {
      if (newValue) {
        this.isLoading = true
        this.fetchItem(newValue).finally(() => {
          this.isLoading = false
        })
      } else {
        this.resetForm()
      }
    },
    isSidebarActive(newValue) {
      if (newValue) {
        this.fetchCountries()
        if (!this.itemId) {
          this.resetForm()
        }
      } else {
        this.resetForm()
      }
    },
    customerId(newValue) {
      if (this.itemData) {
        this.itemData.customer_id = newValue
      }
    },
  },
  mounted() {
    // Set value of refs
    this.refs.value = this.$refs
  },
  setup(props, ctx) {
    // TODO: Implement correctly
    // TODO: Use from schema by BE
    const STORE_MODULE_NAME = addressStoreModule.name
    const CUSTOMER_STORE_MODULE_NAME = 'customers'

    // Register modules
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, addressStoreModule)
    if (!store.hasModule(CUSTOMER_STORE_MODULE_NAME)) store.registerModule(CUSTOMER_STORE_MODULE_NAME, customerStoreModule)

    onMounted(() => {
      if (!store.hasModule(CUSTOMER_STORE_MODULE_NAME)) store.registerModule(CUSTOMER_STORE_MODULE_NAME, customerStoreModule)
    })

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
      if (store.hasModule(CUSTOMER_STORE_MODULE_NAME)) store.unregisterModule(CUSTOMER_STORE_MODULE_NAME)
    })

    // Data definition
    const refs = ref({})
    const emptyItemData = {
      id: null,
      customer_id: props.customerId,
      first_name: null,
      last_name: null,
      type: 'HOME',
      country_code: null,
      state_id: null,
      city_name: null,
      line_1: null,
      line_2: null,
      lat: null,
      long: null,
      short_address: null,
      landmark: null,
      postal_code: null,
      mobile_code: null,
      mobile: null,
      email: null,
    }
    const itemData = ref({})
    const isLoading = ref(true)
    const countryOptions = ref([])
    const stateOptions = ref([])

    const fetchCountries = () => {
      store.dispatch('customers/fetchCountries')
        .then(response => { countryOptions.value = response.data.data })
        .catch(() => {
          // Silently fail if countries can't be loaded
        })
    }

    const fetchStates = country => {
      if (!country) {
        stateOptions.value = []
        return
      }
      store.dispatch('customers/fetchStates', country)
        .then(response => { stateOptions.value = response.data.data })
        .catch(() => {
          // Silently fail if countries can't be loaded
        })
    }

    const resetItemData = () => {
      itemData.value = JSON.parse(JSON.stringify(emptyItemData))
      itemData.value.customer_id = props.customerId

      // Pre-fill data from defaultData
      if (props.defaultData) {
        ['first_name', 'last_name', 'mobile_code', 'mobile', 'country_code'].forEach(key => {
          if (props.defaultData[key]) itemData.value[key] = props.defaultData[key]
          if (key === 'country_code') {
            fetchStates(props.defaultData[key])
          }
        })
      }

      isLoading.value = false
      ctx.emit('update:item-id', null)
    }

    resetItemData()

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetItemData)

    const { validationErrors, fetch, save } = useModelUpdate(
      STORE_MODULE_NAME,
      refs,
      itemData,
      () => {
        ctx.emit('refetch-data')
        ctx.emit('update:is-sidebar-active', false)
      },
      null,
      null,
      () => {
        // refs.value.value.sidebar.hide()
        itemData.value = JSON.parse(JSON.stringify(emptyItemData))

        ctx.emit('update:is-sidebar-active', false)
      },
    )

    const fetchItem = () => {
      if (itemData.value.id !== props.itemId) {
        itemData.value.id = props.itemId
        return fetch()
      }
      return null
    }

    const onSubmit = () => {
      isLoading.value = true
      save(true,
        () => {
          isLoading.value = false
        }, () => {
          isLoading.value = false
        }, () => {
          isLoading.value = false
        })
    }

    return {
      itemData,
      fetchItem,
      validationErrors,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
      fetchCountries,
      fetchStates,

      refs,
      isLoading,
      countryOptions,
      stateOptions,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
